import React, { useState, useMemo, useCallback } from 'react';
import { Table as LegoTable } from '@lucid/lego';
import { createColumns, FseCase, createFSEDetail, formatCaseTitle } from './utils';
import Details from './Details/Details';
import { FseTechSupportCase } from '../../../../api/services/lupp/fse/dto.fse';

interface TableProps {
  theme: 'light' | 'dark';
  data: FseTechSupportCase[];
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
}

const Table: React.FC<TableProps> = ({
  theme,
  data,
  totalCount,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  searchQuery,
  onSearchChange,
}) => {
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<FseCase | null>(null);
  const [selectedCaseDetail, setSelectedCaseDetail] = useState<unknown>(null);

  const handleEditClick = useCallback((record: FseCase) => {
    setSelectedCase(record);
    setSelectedCaseDetail(createFSEDetail(record));
    setIsSliderOpen(true);
  }, []);

  const columns = useMemo(
    () => createColumns({ theme, onEditClick: handleEditClick }),
    [theme, handleEditClick]
  );

  return (
    <>
      <LegoTable
        columns={columns as never}
        data={data as never[]}
        theme={theme === 'light' ? 'dark' : 'light'}
        pagination={{
          enabled: true,
          total: totalCount,
          current: currentPage,
          pageSize,
          onChange: (page) => onPageChange?.(page),
          onPageSizeChange,
        }}
        search={{
          placeholder:
            'Search by Case Number, VIN, Model, Status, Priority, Owner, Support Type, or Description',
          value: searchQuery,
          onChange: onSearchChange,
        }}
        emptyState={{
          message: searchQuery.trim()
            ? 'No results found! Please update your query'
            : 'No tech support cases available',
        }}
        columnConfig={{ enabled: true }}
        selection={{ enabled: false }}
        gaComponent="fse-tech-support-table"
        dataTestId="fse-tech-support-table"
      />
      <Details
        theme={theme}
        isOpen={isSliderOpen}
        onClose={() => {
          setIsSliderOpen(false);
          setSelectedCase(null);
          setSelectedCaseDetail(null);
        }}
        caseFSEDetail={selectedCaseDetail as never}
        caseName={selectedCase ? formatCaseTitle(selectedCase.techSupportIdentifier) : ''}
      />
    </>
  );
};

export default Table;
